import DebugOverlay from "pages/dynamic-app-page/DebugOvverlay";
import WholePageCover from "pages/whole-page-cover/WholePageCover";
import React, { createContext, useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { useTestingSimulation } from "../hooks/useTestingSimulation";
import { determinePriority } from "helpers/determinePriority";

export const CommunicationContext = createContext(null);

// Error Boundary Class
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Update state to display fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // You can log the error to an error reporting service here
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    // Call the callback to update errors state in CommunicationProvider
    if (this.props.onError) {
      this.setState({
        error: errorInfo,
      });
      this.props.onError({ message: errorInfo });
    }
  }

  render() {
    if (this.state.hasError) {
      return <WholePageCover errors={{ error: this.state.error }} />;
    }

    return this.props.children;
  }
}

export const CommunicationProvider = ({ children }) => {
  const sendMessageNoderclRef = useRef();
  const [errors, setErrors] = useState({});
  const [defaultComponent, setDefaultComponent] = useState({
    component: "DisplayScreen",
    data: {
      title: "This screen will be replaced when you call our UI components",
      subtitle: "connection to the server is established",
      show_loader: false,
      theme: "DARK",
      custom_style: {},
    },
  });
  const [components, setComponents] = useState([]);
  const [debugMessage, setDebugMessage] = useState("");
  const componentQueue = useRef([]);

  // Set up testing simulation hooks in dev mode
  useTestingSimulation({ setComponents, componentQueue, setErrors });

  useEffect(() => {
    const socket = io("http://localhost:3001", {
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 5000,
      timeout: 10000,
      autoConnect: true,
      transports: ["websocket", "polling"],
    });

    console.log("Socket connection initiated:", socket);

    socket.on("connect", (socket) => {
      console.log("Connected to socket io server", socket.id);
      setDebugMessage((prev) => (prev.startsWith("Socket") ? "" : prev));
    });

    socket.on("connect_error", (error) => {
      console.error("Connection error:", error);
      setDebugMessage("Socket: Connection error: " + JSON.stringify(error));
    });

    socket.on("disconnect", (reason) => {
      console.error("Disconnected from server - Reason:", reason);
      setDebugMessage("Socket: Disconnected from server - Reason: " + JSON.stringify(reason));
    });

    const handleComponentFinished = (uid) => {
      const componentToFinish = componentQueue.current.find((comp) => comp.uid === uid);

      const updateComponents = () => {
        setComponents((prev) => {
          // Remove the finished component
          const filteredComponents = prev.filter((comp) => comp.uid !== uid);

          // If there are remaining components, show the one with highest priority
          let returnData = [];
          if (filteredComponents.length > 0)
            returnData = filteredComponents.sort((a, b) => b.priority - a.priority);
          componentQueue.current = returnData;
          return returnData;
        });
      };

      if (componentToFinish?.data?.close_delay) {
        setTimeout(() => {
          updateComponents();
        }, componentToFinish.data.close_delay);
      } else updateComponents();
    };

    const handleMessage = (data) => {
      if (
        data?.callback_type === "finished" ||
        (data?.callback_type === "response" && !data?.keep_open)
      )
        handleComponentFinished(data.id);
    };

    const hadnleRclnodemessage = (data) => {
      if (data?.reset) {
        setComponents([]);
        componentQueue.current = [];
        setErrors({});
        return;
      }

      // Validate priority
      const priority = determinePriority(data);

      const finishComponent = (id) => {
        socket.emit("chest_client-rclnode", JSON.stringify({ callback_type: "finished", id }));
      };

      setComponents((prev) => {
        // Check if this is an update to existing component (has component_id)
        const isUpdate = data?.component_id;
        const existingComponentIndex = isUpdate
          ? prev.findIndex((comp) => comp.component_id === data.component_id)
          : -1;

        if (isUpdate && existingComponentIndex !== -1) {
          // UPDATE EXISTING COMPONENT
          const existingComponent = prev[existingComponentIndex];

          // Merge data (partial update)
          const parsedNewData =
            typeof data?.data === "string" ? JSON.parse(data?.data) : data?.data;
          const mergedData = { ...existingComponent.data, ...parsedNewData };

          const updatedComponent = {
            ...existingComponent,
            data: mergedData,
            priority: priority, // Allow priority updates
          };

          console.log(`✅ Updated existing component with ID: ${data.component_id}`);
          socket.emit(
            "chest_client-rclnode-logs",
            `Successfully updated component: ${data.component_id}`,
          );

          // Replace the existing component
          const newComponents = [...prev];
          newComponents[existingComponentIndex] = updatedComponent;

          // Re-sort by priority
          const returnData = newComponents.sort((a, b) => b.priority - a.priority);
          componentQueue.current = returnData;
          return returnData;
        }

        // Check if there's an active higher priority component
        const highestPriorityComponent = prev[0];

        if (highestPriorityComponent && priority < highestPriorityComponent.priority) {
          // Reject lower priority component if higher priority is active
          console.log("🚀 ~ setComponents ~ Rejecting lower priority component");
          socket.emit(
            "chest_client-rclnode",
            JSON.stringify({
              callback_type: "rejected",
              message: `Component rejected due to lower priority (${priority}) than active component (${highestPriorityComponent.priority})`,
              id: data?.uid,
            }),
          );
          componentQueue.current = prev;
          return prev;
        }

        const newComponent = {
          component: data?.component,
          data: typeof data?.data === "string" ? JSON.parse(data?.data) : data?.data,
          uid: data?.uid,
          component_id: data?.component_id, // Store component_id for future updates
          priority: priority,
        };

        if (data?.override) {
          // Remove components with same and lower priority
          const filteredComponents = prev.filter((comp) => comp.priority > newComponent.priority);

          // Finish components with same or lower priority
          const componentsToRemove = prev.filter((comp) => comp.priority <= newComponent.priority);
          componentsToRemove.forEach((comp) => {
            finishComponent(comp.uid);
          });

          const returnData = [newComponent, ...filteredComponents];
          componentQueue.current = returnData;
          return returnData;
        } else {
          // Remove components with same priority
          const filteredComponents = prev.filter((comp) => comp.priority !== newComponent.priority);

          // Finish component with same priority
          const componentToFinish = prev.find((comp) => comp.priority === newComponent.priority);
          if (componentToFinish) finishComponent(componentToFinish.uid);

          const returnData = [...filteredComponents, newComponent].sort(
            (a, b) => b.priority - a.priority,
          );
          componentQueue.current = returnData;
          return returnData;
        }
      });
    };

    hadnleRclnodemessage({
      component: "SplitScreen",
      priority: 2,
      component_id: "SplitScreen",
      data: {
        theme: "WHITE",
        orientation: "vertical",
        back_button: "Back",
        finish_button: "Finish",
        first_component: {
          component: "Choice",
          data: {
            title: "Please select package destination",
            data: [
              {
                id: 1,
                name: "Unit 1",
                custom_style: {
                  width: 369,
                  height: 135,
                  animation: "none",
                  "box-shadow": "none",
                },
              },
              {
                id: 2,
                name: "Unit 2",
                custom_style: {
                  width: 369,
                  height: 135,
                  animation: "none",
                  "box-shadow": "none",
                },
              },
              {
                id: 3,
                name: "Unit 3",
                custom_style: {
                  width: 369,
                  height: 135,
                  animation: "none",
                  "box-shadow": "none",
                },
              },
            ],
            title_size: "SMALL",
            max_items_shown: 0,
          },
        },
        second_component: {
          component: "Choice",
          data: {
            title: "Please select package type",
            data: [
              {
                id: 1,
                name: "Controlled",
                custom_style: {
                  width: 369,
                  height: 135,
                  animation: "none",
                  "box-shadow": "none",
                },
              },
              {
                id: 2,
                name: "Not Controlled",
                custom_style: {
                  width: 369,
                  height: 135,
                  animation: "none",
                  "box-shadow": "none",
                },
              },
            ],
            title_size: "SMALL",
            max_items_shown: 0,
          },
        },
      },
    });

    socket.on("rclnode-chest_client", (data) => {
      console.log("Received from rclnode:", data?.component, data);
      hadnleRclnodemessage(data);
    });

    sendMessageNoderclRef.current = {
      sendMessage: (data) => {
        const dataForNode = {
          ...data,
          stamp: {
            sec: Math.floor(Date.now() / 1000),
            nanosec: (Date.now() % 1000) * 1e6,
          },
        };
        console.log("🚀 ~ this data is sent to rclnodejs", dataForNode);
        handleMessage(data);
        socket.emit("chest_client-rclnode", JSON.stringify(dataForNode));
      },
    };

    socket.on("rclnode-chest_client-debug_msg", (msg) => {
      console.log("Received from rclnode debug message:", msg);
      setDebugMessage(msg?.data);
    });

    socket.on("rclnode-chest_client-default_component", (data) => {
      console.log("Received default component from rclnode:", data);
      setDefaultComponent(data);
    });

    socket.on("rclnode-chest_client-finish", (data) => {
      console.log("Received finish command:", data);
      setComponents((prev) => {
        const componentsToFinish = prev.filter((comp) =>
          data.include_lower ? comp.priority <= data.priority : comp.priority === data.priority,
        );

        if (componentsToFinish.length === 0) {
          socket.emit("chest_client-rclnode-logs", "No components to finish");
        }

        // Emit finished event for each component being removed
        componentsToFinish.forEach((comp) => {
          socket.emit(
            "chest_client-rclnode",
            JSON.stringify({
              callback_type: "finished",
              id: comp.uid,
            }),
          );
        });

        // Keep only components that weren't finished
        const returnData = prev.filter((comp) =>
          data.include_lower ? comp.priority > data.priority : comp.priority !== data.priority,
        );
        componentQueue.current = returnData;
        return returnData;
      });
    });

    // Frontend
    window.addEventListener("online", function () {
      console.log("Browser came online");
      socket.connect();
    });

    window.addEventListener("offline", function () {
      console.log("Browser went offline");
    });

    return () => {
      socket.close();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CommunicationContext.Provider
      value={{
        components,
        sendMessage: (data) => {
          sendMessageNoderclRef.current?.sendMessage(data);
        },
        errors,
        setErrors,
        defaultComponent,
      }}
    >
      {/* Error Boundary Wrapper */}
      <ErrorBoundary onError={setErrors}>
        {Object.keys(errors).length ? <WholePageCover errors={errors} /> : children}
      </ErrorBoundary>
      <DebugOverlay debugMessage={debugMessage} />
    </CommunicationContext.Provider>
  );
};
